<template>
  <div>
    <b-card>
      <TitleTable titleTable="Add-ons GLP-1" iconTable="fas fa-plus-circle" />
      <b-row class="wl-filters m-1 mb-1 align-items-start">
        <b-col cols="12" md="3">
          <b-form-group label="Facility" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filterFacilitiesId"
              :options="facilityOptions"
              :normalizer="normalizer"
              :multiple="true"
              :clearable="true"
              placeholder="All facilities"
              :disabled="facilityOptionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="3">
          <b-form-group label="Status" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-select v-model="filterActive" :options="activeFilterOptions">
              <template #first>
                <b-form-select-option :value="null">All</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6" class="d-flex justify-content-end align-items-end pb-1">
          <b-button
            variant="outline-primary"
            class="wl-filter-btn mt-2 mt-md-0"
            :disabled="facilityOptionsLoading"
            @click="load">
            Filter
          </b-button>
        </b-col>
      </b-row>
      <b-row class="m-1 mb-3">
        <b-col cols="12" class="wl-addons-actions-row d-flex justify-content-end align-items-center pt-2 border-top">
          <GoBack class="mr-2" />
          <b-button variant="primary" @click="openAdd">Add</b-button>
        </b-col>
      </b-row>
      <b-table
        :items="items"
        :fields="fields"
        bordered
        responsive
        show-empty
        empty-text="No add-ons found">
        <template #cell(facility_id)="data">
          <span :class="{ 'text-muted font-italic': !hasFacility(data.item) }">
            {{ facilityDisplay(data.item) }}
          </span>
        </template>
        <template #cell(description)="data">
          <span :class="{ 'text-muted font-italic': isNullish(data.value) }">
            {{ displayValue(data.value) }}
          </span>
        </template>
        <template #cell(price)="data">
          <span :class="{ 'text-muted font-italic': isNullish(data.value) }">
            {{ displayPrice(data.value) }}
          </span>
        </template>
        <template #cell(active)="data">
          <b-badge :variant="data.value ? 'success' : 'secondary'">
            {{ data.value ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" class="mr-1" @click="openEdit(data.item)">
            <i class="fas fa-pen"></i>
          </b-button>
          <b-button size="sm" variant="outline-danger" @click="remove(data.item)">
            <i class="fas fa-trash-alt"></i>
          </b-button>
        </template>
      </b-table>
    </b-card>

    <WlFormSidebar
      :visible.sync="sidebarVisible"
      sidebar-id="wl-addon-sidebar"
      :title="editId ? 'Edit add-on' : 'New add-on'"
      :loading="saving"
      @submit="save"
      @close="onSidebarClose">
      <b-form-group label="Name">
        <b-form-input v-model="form.name" placeholder="Add-on name" />
      </b-form-group>
      <b-form-group label="Facility">
        <treeselect
          v-model="form.facility_id"
          :options="facilityOptions"
          :normalizer="normalizer"
          :clearable="true"
          placeholder="Global (all facilities)"
          :disabled="facilityOptionsLoading || saving" />
        <small class="text-muted d-block mt-1">Leave empty for a global add-on available at all facilities.</small>
      </b-form-group>
      <b-form-group label="Description">
        <b-form-textarea v-model="form.description" rows="2" placeholder="Optional" />
      </b-form-group>
      <b-form-group label="Price">
        <b-form-input v-model="form.price" type="number" step="0.01" min="0" />
      </b-form-group>
      <b-form-group label="Active">
        <b-form-checkbox v-model="form.active" switch />
      </b-form-group>
    </WlFormSidebar>

    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BBadge,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BFormSelect,
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import TitleTable from '@/components/TitleTable.vue'
import GoBack from '@/components/ButtonBack.vue'
import ToastNotification from '@/components/ToastNotification'
import WlFormSidebar from '../components/WlFormSidebar.vue'
import wlAddonsApi from '@/core/services/api/weightloss-inventory/addons'
import { resolveNumericFacilityId } from '@/core/services/api/weightloss-inventory/facility'
import axiosF from '@/core/services/api/admin/facilities'
import Swal from 'sweetalert2'

export default {
  name: 'TableWlAddons',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BBadge,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BFormSelect,
    Treeselect,
    TitleTable,
    GoBack,
    ToastNotification,
    WlFormSidebar,
  },
  data() {
    return {
      normalizer(node) {
        return {
          id: node.id,
          label: node.name,
        }
      },
      items: [],
      filterFacilitiesId: [],
      filterActive: true,
      activeFilterOptions: [
        { value: true, text: 'Active' },
        { value: false, text: 'Inactive' },
      ],
      facilityOptions: [],
      facilityOptionsLoading: false,
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'Name' },
        { key: 'facility_id', label: 'Facility' },
        { key: 'description', label: 'Description' },
        { key: 'price', label: 'Price' },
        { key: 'active', label: 'Status' },
        { key: 'actions', label: '' },
      ],
      sidebarVisible: false,
      saving: false,
      editId: null,
      form: {
        name: '',
        facility_id: null,
        description: '',
        price: '',
        active: true,
      },
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      await this.loadFacilities()
      this.load()
    },
    async loadFacilities() {
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(item => ({
          id: item.id,
          name: item.name,
        }))
      } catch (e) {
        this.facilityOptions = []
        this.$refs.toast?.danger(e?.message || 'Could not load facilities')
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    isNullish(value) {
      return value === null || value === undefined || value === ''
    },
    displayValue(value) {
      return this.isNullish(value) ? 'null' : value
    },
    displayPrice(value) {
      if (this.isNullish(value)) return 'null'
      const n = Number(value)
      return Number.isFinite(n) ? n.toFixed(2) : value
    },
    hasFacility(item) {
      if (item?.facility?.name) return true
      const id = item?.facility_id ?? item?.facilities_id ?? item?.facility?.id
      return id != null && id !== ''
    },
    facilityDisplay(item) {
      if (item?.facility?.name) return item.facility.name
      const id = item?.facility_id ?? item?.facilities_id ?? item?.facility?.id
      if (id != null && id !== '') {
        const f = this.facilityOptions.find(
          x => x.id === id || x.id === Number(id)
        )
        return f ? f.name : `#${id}`
      }
      return 'Global'
    },
    onSidebarClose() {
      this.editId = null
    },
    openAdd() {
      this.editId = null
      const defaultFacility = resolveNumericFacilityId()
      this.form = {
        name: '',
        facility_id: defaultFacility ? Number(defaultFacility) : null,
        description: '',
        price: '',
        active: true,
      }
      this.sidebarVisible = true
    },
    openEdit(item) {
      this.editId = item.id
      this.form = {
        name: item.name || '',
        facility_id: item.facility_id != null ? Number(item.facility_id) : null,
        description: item.description || '',
        price: item.price != null ? item.price : '',
        active: item.active !== false,
      }
      this.sidebarVisible = true
    },
    async load() {
      try {
        const params = {}
        if (this.filterFacilitiesId && this.filterFacilitiesId.length) {
          params.facilities_id = this.filterFacilitiesId
            .map(v => Number(v))
            .filter(v => Number.isFinite(v))
        }
        if (this.filterActive != null) params.active = this.filterActive
        this.items = await wlAddonsApi.list(params)
      } catch (e) {
        this.$refs.toast.danger(e.message)
      }
    },
    async save() {
      if (!this.form.name || !String(this.form.name).trim()) {
        this.$refs.toast.danger('Name is required')
        return
      }
      this.saving = true
      try {
        const payload = {
          name: String(this.form.name).trim(),
          description: this.form.description?.trim() || null,
          price: this.form.price === '' || this.form.price == null ? null : Number(this.form.price),
          active: !!this.form.active,
        }
        if (this.form.facility_id != null && this.form.facility_id !== '') {
          payload.facility_id = Number(this.form.facility_id)
        } else {
          payload.facility_id = null
        }
        if (this.editId) {
          await wlAddonsApi.update(this.editId, payload)
          this.$refs.toast.success('Add-on updated')
        } else {
          await wlAddonsApi.create(payload)
          this.$refs.toast.success('Add-on created')
        }
        this.sidebarVisible = false
        this.editId = null
        this.load()
      } catch (e) {
        this.$refs.toast.danger(e.message)
      } finally {
        this.saving = false
      }
    },
    remove(item) {
      Swal.fire({
        title: 'Delete add-on?',
        text: item.name,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
      }).then(async r => {
        if (!r.value) return
        try {
          await wlAddonsApi.remove(item.id)
          this.$refs.toast.success('Deleted')
          this.load()
        } catch (e) {
          this.$refs.toast.danger(e.message)
        }
      })
    },
  },
}
</script>

<style scoped>
.wl-filter-btn {
  min-width: 7rem;
}
</style>
